import {
  type ScheduledCrossClientQuery,
  type ScheduledCrossClientQueryCreate,
  type ScheduledCrossClientQuerySummary,
  type ScheduledCrossClientQueryUpdate,
} from '../../models/ScheduledCrossClientQuery'
import { type BasePageRequest, type BasePageResponse, type BaseRequest } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { createApi } from '@reduxjs/toolkit/query/react'

const _AI_MANAGEMENT_BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

interface CreateScheduledCrossClientQueryRequest extends BaseRequest {
  request: ScheduledCrossClientQueryCreate
}

interface ScheduledCrossClientQueryRequest extends BaseRequest {
  scheduledQueryId: string
}

interface GetScheduledCrossClientQueriesRequest extends BasePageRequest {
  active?: boolean
  managedById?: string
}

interface UpdateScheduledCrossClientQueryRequest extends ScheduledCrossClientQueryRequest {
  request: ScheduledCrossClientQueryUpdate
}

export const scheduledCrossClientApi = createApi({
  reducerPath: 'scheduledCrossClientApi',
  baseQuery: baseQueryWithTokenRefresh(_AI_MANAGEMENT_BASE_URL),
  tagTypes: ['ScheduledCrossClientQuery'],
  endpoints: (builder) => ({
    createScheduledCrossClientQuery: builder.mutation<ScheduledCrossClientQuery, CreateScheduledCrossClientQueryRequest>({
      query: ({ accountId, request }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { data: ScheduledCrossClientQuery }) => response.data,
      invalidatesTags: [{ type: 'ScheduledCrossClientQuery', id: 'LIST' }],
    }),
    getScheduledCrossClientQuery: builder.query<ScheduledCrossClientQuery, ScheduledCrossClientQueryRequest>({
      query: ({ accountId, scheduledQueryId }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/${scheduledQueryId}`,
      }),
      transformResponse: (response: { data: ScheduledCrossClientQuery }) => response.data,
      providesTags: (_, __, { scheduledQueryId }) => [{ type: 'ScheduledCrossClientQuery', id: scheduledQueryId }],
    }),
    getScheduledCrossClientQuerySummaries: builder.query<BasePageResponse<ScheduledCrossClientQuerySummary[]>, GetScheduledCrossClientQueriesRequest>({
      query: ({ accountId, pageRequest, active, managedById }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/`,
        params: {
          page: pageRequest.page,
          size: pageRequest.size,
          ...(active !== undefined ? { active } : {}),
          ...(managedById ? { managedById } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'ScheduledCrossClientQuery' as const, id })), { type: 'ScheduledCrossClientQuery', id: 'LIST' }]
          : [{ type: 'ScheduledCrossClientQuery', id: 'LIST' }],
    }),
    getScheduledCrossClientQueries: builder.query<BasePageResponse<ScheduledCrossClientQuery[]>, GetScheduledCrossClientQueriesRequest>({
      query: ({ accountId, pageRequest, active, managedById }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/`,
        params: {
          page: pageRequest.page,
          size: pageRequest.size,
          expand: true,
          ...(active !== undefined ? { active } : {}),
          ...(managedById ? { managedById } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'ScheduledCrossClientQuery' as const, id })), { type: 'ScheduledCrossClientQuery', id: 'LIST' }]
          : [{ type: 'ScheduledCrossClientQuery', id: 'LIST' }],
    }),
    updateScheduledCrossClientQuery: builder.mutation<ScheduledCrossClientQuery, UpdateScheduledCrossClientQueryRequest>({
      query: ({ accountId, scheduledQueryId, request }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/${scheduledQueryId}`,
        method: 'PATCH',
        body: request,
      }),
      transformResponse: (response: { data: ScheduledCrossClientQuery }) => response.data,
      invalidatesTags: (_, __, { scheduledQueryId }) => [
        { type: 'ScheduledCrossClientQuery', id: scheduledQueryId },
        { type: 'ScheduledCrossClientQuery', id: 'LIST' },
      ],
    }),
    deleteScheduledCrossClientQuery: builder.mutation<void, ScheduledCrossClientQueryRequest>({
      query: ({ accountId, scheduledQueryId }) => ({
        url: `/accounts/${accountId}/cross-client/scheduled-queries/${scheduledQueryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { scheduledQueryId }) => [
        { type: 'ScheduledCrossClientQuery', id: scheduledQueryId },
        { type: 'ScheduledCrossClientQuery', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useCreateScheduledCrossClientQueryMutation,
  useGetScheduledCrossClientQueryQuery,
  useLazyGetScheduledCrossClientQueryQuery,
  useGetScheduledCrossClientQuerySummariesQuery,
  useLazyGetScheduledCrossClientQuerySummariesQuery,
  useGetScheduledCrossClientQueriesQuery,
  useLazyGetScheduledCrossClientQueriesQuery,
  useUpdateScheduledCrossClientQueryMutation,
  useDeleteScheduledCrossClientQueryMutation,
} = scheduledCrossClientApi
